// src/index.ts
import { compileVocabulary, isInitials, canonicaliseSpec, SCORE_MAX } from "@forgesworn/joystick-protocol";

// src/foregather.ts
async function authorityDecision(decision, signal) {
  if (!signal) return decision;
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason);
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    signal.addEventListener("abort", abort, { once: true });
    Promise.resolve(decision).then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}
async function transferControllerInvitation({ host, flow, authorise, signal }) {
  signal?.throwIfAborted();
  if (typeof authorise !== "function" || host.closed || await authorityDecision(authorise(), signal) !== true) throw new Error("Controller invitation not authorised");
  signal?.throwIfAborted();
  if (host.closed) throw new Error("Controller host closed");
  const payload = new TextEncoder().encode(host.pairingUrl);
  try {
    return await flow.send({ payload, signal });
  } finally {
    payload.fill(0);
  }
}

// src/index.ts
var READY_OPEN = 1;
function startControllerHost(opts) {
  const WS = opts.WebSocketImpl ?? globalThis.WebSocket;
  const setT = opts.setTimeoutImpl ?? ((h, ms) => globalThis.setTimeout(h, ms));
  const clearT = opts.clearTimeoutImpl ?? ((id) => globalThis.clearTimeout(id));
  if (typeof opts.ws !== "string" || !opts.ws) {
    throw new Error("startControllerHost needs `ws`, the wss:// URL of your own controller relay. The SDK has no default relay: run packages/broker and pass its URL.");
  }
  const wsUrl = opts.ws;
  const vocabulary = compileVocabulary(opts.vocabulary);
  const warned = /* @__PURE__ */ new Set();
  const warnOnce = (what) => {
    if (warned.has(what)) return;
    warned.add(what);
    try {
      console.warn(`[joystick-host] the relay will not forward ${what}; it is not in its vocabulary`);
    } catch {
    }
  };
  const sessionId = opts.sessionId ?? randomSessionId(opts.protocolVersion === 1 ? 4 : 6);
  const protocolVersion = opts.protocolVersion ?? 2;
  if (![1, 2].includes(protocolVersion)) throw Error("Invalid controller version");
  if (!/^[a-z0-9_-]{4,128}$/i.test(sessionId) || protocolVersion === 2 && sessionId.length < 6) throw Error("Invalid controller session");
  const hostToken = protocolVersion === 2 ? opts.hostToken ?? randomToken() : void 0;
  if (hostToken !== void 0 && (typeof hostToken !== "string" || !/^[a-f0-9]{64}$/.test(hostToken))) throw Error("Invalid host credential");
  const multi = opts.multi ?? false;
  const pwaUrl = opts.pwaUrl ?? defaultPwaUrl();
  const pairingUrl = encodePairingUrl({ sessionId, ws: wsUrl, ...protocolVersion === 2 ? { version: 2 } : {} }, pwaUrl);
  let ws = null;
  let closed = false;
  let paired = false;
  let lastInputAt = 0;
  let reconnectAttempt = 0;
  let hostReady = protocolVersion === 1;
  let activeSpec = null;
  const players = /* @__PURE__ */ new Map();
  let statusCb = null;
  let joinCb = null;
  let leaveCb = null;
  let suspendCb = null;
  let resumeCb = null;
  const suspendedTimers = /* @__PURE__ */ new Map();
  const pendingAwards = /* @__PURE__ */ new Map();
  let inputCb = null;
  const phoneContextCbs = /* @__PURE__ */ new Set();
  const fireStatus = (s) => {
    try {
      statusCb?.(s);
    } catch {
    }
  };
  const fireJoin = (p) => {
    try {
      joinCb?.(p);
    } catch {
    }
  };
  const fireLeave = (p) => {
    try {
      leaveCb?.(p);
    } catch {
    }
  };
  const fireInput = (f) => {
    try {
      inputCb?.(f);
    } catch {
    }
  };
  const firePhoneContext = (p, ctx) => {
    for (const cb of phoneContextCbs) {
      try {
        cb(p, ctx);
      } catch {
      }
    }
  };
  const isOpen = () => !!ws && ws.readyState === READY_OPEN;
  const sendRaw = (frame) => {
    if (!isOpen() || !ws || !hostReady) return;
    try {
      ws.send(JSON.stringify(frame));
    } catch {
    }
  };
  const sendSpec = () => {
    if (!activeSpec) return;
    sendRaw({ type: "controller-spec", spec: activeSpec });
  };
  const upsertPlayer = (p) => {
    let existing = players.get(p);
    if (!existing) {
      existing = { p, connected: true, phoneContext: null };
      players.set(p, existing);
    }
    return existing;
  };
  const handlePeerUp = (p) => {
    const existing = players.get(p);
    const resumed = existing?.connected === false;
    clearT(suspendedTimers.get(p));
    suspendedTimers.delete(p);
    const player = upsertPlayer(p);
    player.connected = true;
    if (!paired) {
      paired = true;
      fireStatus({ kind: "paired" });
    }
    if (activeSpec) {
      if (multi) sendRaw({ type: "controller-spec", spec: activeSpec, p });
      else sendSpec();
    }
    if (resumed) {
      try {
        resumeCb?.(player);
      } catch {
      }
    } else if (!existing || !multi) fireJoin(player);
    const award = pendingAwards.get(p);
    if (award) {
      pendingAwards.delete(p);
      sendRaw(award);
    }
  };
  const handlePeerSuspended = (p, graceMs = 15e3) => {
    const player = players.get(p);
    if (!player) return;
    clearT(suspendedTimers.get(p));
    if (player.connected) {
      player.connected = false;
      try {
        suspendCb?.(player);
      } catch {
      }
    }
    suspendedTimers.set(p, setT(() => handlePeerDown(p), Math.max(0, Math.min(6e4, graceMs))));
    if (![...players.values()].some((player2) => player2.connected)) {
      paired = false;
      fireStatus({ kind: "waiting" });
    }
  };
  const handlePeerDown = (p) => {
    clearT(suspendedTimers.get(p));
    suspendedTimers.delete(p);
    pendingAwards.delete(p);
    const player = players.get(p);
    if (!player) return;
    players.delete(p);
    fireLeave(player);
    if (![...players.values()].some((player2) => player2.connected)) {
      if (paired) {
        paired = false;
        fireStatus({ kind: "waiting" });
      }
    }
  };
  const handlePhoneContext = (obj, p) => {
    const total = typeof obj.tournamentTotal === "number" && Number.isFinite(obj.tournamentTotal) ? Math.max(0, Math.floor(obj.tournamentTotal)) : null;
    if (total === null) return;
    const name = isInitials(obj.name) ? obj.name : void 0;
    const player = players.get(p);
    if (!player?.connected) return;
    const next = { tournamentTotal: total, ...name ? { name } : {} };
    const prev = player.phoneContext;
    const changed = !prev || prev.tournamentTotal !== next.tournamentTotal || prev.name !== next.name;
    player.phoneContext = next;
    if (changed) firePhoneContext(p, next);
  };
  const handleFrame = (obj) => {
    if (obj.type === "host-ready" && protocolVersion === 2 && obj.version === 2) {
      hostReady = true;
      fireStatus({ kind: "waiting" });
      return;
    }
    if (obj.type === "error" && obj.code === "host-ownership") {
      close();
      return;
    }
    if (!hostReady) return;
    const p = typeof obj.p === "number" ? obj.p : 0;
    if (!Number.isSafeInteger(p) || p < 0 || p > 63) return;
    if (typeof obj.type === "string") {
      switch (obj.type) {
        case "peer-up":
          handlePeerUp(p);
          return;
        case "peer-down":
          handlePeerDown(p);
          return;
        case "peer-suspended":
          if (multi) handlePeerSuspended(p, typeof obj.graceMs === "number" && Number.isFinite(obj.graceMs) ? obj.graceMs : void 0);
          return;
        case "phone-context":
          handlePhoneContext(obj, p);
          return;
        default:
          return;
      }
    }
    if (typeof obj.k !== "string" || typeof obj.v !== "string" || !players.get(p)?.connected) return;
    const frame = { k: obj.k, v: obj.v };
    if (typeof obj.p === "number") frame.p = obj.p;
    lastInputAt = nowMs();
    fireInput(frame);
  };
  const connect = () => {
    if (closed) return;
    fireStatus({ kind: reconnectAttempt > 0 ? "reconnecting" : "connecting" });
    const endpoint = new URL(wsUrl);
    endpoint.searchParams.set("s", sessionId);
    endpoint.searchParams.set("r", "host");
    if (multi) endpoint.searchParams.set("multi", "1");
    if (protocolVersion === 2) endpoint.searchParams.set("v", "2");
    else endpoint.searchParams.delete("v");
    hostReady = protocolVersion === 1;
    try {
      ws = new WS(endpoint.href);
    } catch {
      scheduleReconnect();
      return;
    }
    const currentSocket = ws;
    ws.onopen = () => {
      if (closed || ws !== currentSocket) {
        currentSocket.close();
        return;
      }
      reconnectAttempt = 0;
      if (protocolVersion === 2) currentSocket.send(JSON.stringify({ type: "host-join", hostToken }));
      else if (!paired) fireStatus({ kind: "waiting" });
    };
    ws.onmessage = (ev) => {
      if (closed || ws !== currentSocket) return;
      const data = typeof ev.data === "string" ? ev.data : "";
      if (!data) return;
      let parsed;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (!parsed || typeof parsed !== "object") return;
      handleFrame(parsed);
    };
    ws.onerror = () => {
    };
    ws.onclose = () => {
      if (ws !== currentSocket) return;
      hostReady = false;
      if (paired) {
        for (const player of [...players.values()]) {
          if (multi) handlePeerSuspended(player.p);
          else handlePeerDown(player.p);
        }
        paired = false;
      }
      scheduleReconnect();
    };
  };
  const scheduleReconnect = () => {
    if (closed) return;
    reconnectAttempt += 1;
    const delay = Math.min(15e3, 500 * Math.pow(1.6, reconnectAttempt)) * (0.75 + Math.random() * 0.5);
    setT(connect, delay);
  };
  connect();
  const setSpec = (spec) => {
    let next = spec;
    if (spec) {
      const canonical = canonicaliseSpec(spec, vocabulary);
      if (!canonical) throw Error("Invalid controller definition");
      for (const field of canonical.stripped) warnOnce(`spec field ${field}`);
      next = canonical.spec;
    }
    if (specEqual(next, activeSpec)) return;
    activeSpec = next;
    if (spec && paired) sendSpec();
  };
  const broadcast = (message) => {
    sendRaw(message);
  };
  const sendToPlayer = (p, message) => {
    if (multi) sendRaw({ ...message, p });
    else sendRaw(message);
  };
  const haptic = (pattern, p) => {
    const frame = { type: "haptic", pattern };
    if (multi) frame.p = p;
    sendRaw(frame);
  };
  const hapticAll = (pattern) => {
    const frame = { type: "haptic", pattern };
    sendRaw(frame);
  };
  const awardScore = (p, score, label) => {
    if (!Number.isFinite(score)) return;
    const frame = { type: "game-score", score: Math.round(score) };
    if (label) {
      if (vocabulary.scoreLabel(label)) frame.label = label;
      else warnOnce(`score label "${label}"`);
    }
    if (frame.score < 0 || frame.score > SCORE_MAX) return;
    if (multi) frame.p = p;
    const player = players.get(p);
    if (player && (!player.connected || !isOpen())) {
      frame.score += pendingAwards.get(p)?.score ?? 0;
      if (Number.isSafeInteger(frame.score)) pendingAwards.set(p, frame);
      return;
    }
    sendRaw(frame);
  };
  const sendSpectatorView = (p, view) => {
    if (!vocabulary.title(view.title)) warnOnce(`spectator title "${view.title}"`);
    const leaderboard = view.leaderboard.slice(0, 8);
    for (const row of leaderboard) if (!vocabulary.rowLabel(row.label)) warnOnce(`spectator row label "${row.label}"`);
    if (view.highlight && !vocabulary.highlight(view.highlight)) warnOnce(`spectator highlight "${view.highlight}"`);
    const frame = {
      type: "spectator-view",
      title: view.title,
      leaderboard,
      ...view.highlight ? { highlight: view.highlight } : {}
    };
    if (multi) frame.p = p;
    sendRaw(frame);
  };
  const close = () => {
    if (closed) return;
    closed = true;
    if (ws) try {
      ws.close();
    } catch {
    }
    for (const player of [...players.values()]) {
      clearT(suspendedTimers.get(player.p));
      players.delete(player.p);
      fireLeave(player);
    }
    suspendedTimers.clear();
    pendingAwards.clear();
    paired = false;
    fireStatus({ kind: "closed" });
  };
  return {
    sessionId,
    ws: wsUrl,
    pairingUrl,
    multi,
    get paired() {
      return paired;
    },
    get closed() {
      return closed;
    },
    get players() {
      return [...players.values()];
    },
    get lastInputAt() {
      return lastInputAt;
    },
    setSpec,
    sendToPlayer,
    broadcast,
    haptic,
    hapticAll,
    awardScore,
    sendSpectatorView,
    onPhoneContext: (cb) => {
      phoneContextCbs.add(cb);
      for (const player of players.values()) {
        if (player.phoneContext) try {
          cb(player.p, player.phoneContext);
        } catch {
        }
      }
      return () => {
        phoneContextCbs.delete(cb);
      };
    },
    close,
    onStatus: (cb) => {
      statusCb = cb;
    },
    onPlayerJoin: (cb) => {
      joinCb = cb;
    },
    onPlayerLeave: (cb) => {
      leaveCb = cb;
    },
    onPlayerSuspend: (cb) => {
      suspendCb = cb;
    },
    onPlayerResume: (cb) => {
      resumeCb = cb;
    },
    onInput: (cb) => {
      inputCb = cb;
    }
  };
}
function specEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
function defaultPwaUrl() {
  const loc = globalThis.location;
  const origin = loc?.origin ?? "";
  return `${origin}/controller`;
}
function encodePairingUrl(token, baseUrl) {
  const params = new URLSearchParams({ s: token.sessionId });
  params.set("w", token.ws);
  if (token.version === 2) params.set("v", "2");
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}${params.toString()}`;
}
function decodePairingUrl(url) {
  try {
    const u = new URL(url);
    if (!["https:", "http:", "joystick:"].includes(u.protocol) || u.username || u.password || u.searchParams.getAll("s").length !== 1 || u.searchParams.getAll("w").length > 1 || u.searchParams.getAll("v").length > 1) return null;
    const s = u.searchParams.get("s");
    if (!s || !/^[a-z0-9_-]{4,128}$/i.test(s)) return null;
    const w = u.searchParams.get("w");
    if (!w) return null;
    const endpoint = new URL(w);
    if (!["ws:", "wss:"].includes(endpoint.protocol) || !endpoint.hostname || endpoint.username || endpoint.password || endpoint.hash || endpoint.search) return null;
    const version = u.searchParams.get("v");
    if (version !== null && version !== "2" || version === "2" && s.length < 6) return null;
    return { sessionId: s, ws: w, ...version === "2" ? { version: 2 } : {} };
  } catch {
    return null;
  }
}
var SESSION_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ";
var SESSION_CODE_BLOCKLIST = [
  "FUCK",
  "FUC",
  "FUK",
  "CUNT",
  "TWAT",
  "WANK",
  "ARSE",
  "CUM",
  "ASS",
  "SEX",
  "FAG",
  "KKK"
];
function randomSessionId(length) {
  for (let attempt = 0; attempt < 16; attempt += 1) {
    const code = drawSessionCode(length);
    if (!SESSION_CODE_BLOCKLIST.some((bad) => code.includes(bad))) return code;
  }
  return drawSessionCode(length);
}
function drawSessionCode(length) {
  const crypto = cryptoFor();
  let code = "";
  while (code.length < length) {
    const buf = new Uint8Array(8);
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (code.length >= length) break;
      if (b < 240) code += SESSION_ALPHABET[b % SESSION_ALPHABET.length];
    }
  }
  return code;
}
function randomToken() {
  const bytes = new Uint8Array(32);
  cryptoFor().getRandomValues(bytes);
  return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
function cryptoFor() {
  const c = globalThis.crypto;
  if (!c) throw new Error("crypto not available");
  return c;
}
function nowMs() {
  const p = globalThis.performance;
  return p ? p.now() : Date.now();
}
export {
  decodePairingUrl,
  encodePairingUrl,
  startControllerHost,
  transferControllerInvitation
};
//# sourceMappingURL=index.js.map